import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { white } from '@Styles/colors';
import { THEME } from '@Theme';
import {
	Attachment,
	AttachmentInfo,
	AttachmentPreview,
	AttachmentRemove,
	Attachments,
} from '@HoudiniUI/ai/Attachments';
import {
	AI_CHAT_FILE_ACCEPT,
	AI_CHAT_MAX_FILE_SIZE,
	AI_CHAT_MAX_FILES,
	type UseAIChatAttachmentsResult,
} from '@HoudiniUI/ai/PromptInput/PromptInputAttachments';
import { Button } from '@HoudiniUI/Button';
import { Checkbox } from '@HoudiniUI/Checkbox';
import { Field, FieldContent, FieldGroup, FieldLabel, FieldTitle } from '@HoudiniUI/Field';
import { Input } from '@HoudiniUI/Input';
import { TextArea } from '@HoudiniUI/TextArea';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { PaperClipIcon } from '@WebComponents/svgs/icons/PaperClipIcon';
import { Avatar } from '@HoudiniUI/Avatar';

const MAX_INSTRUCTIONS_LENGTH = 2000;

export interface AgentBuilderStarterFormSubmission {
	websiteUrl: string;
	useAccountLogo: boolean;
	instructions: string;
}

interface AgentBuilderStarterFormProps {
	defaultWebsiteUrl?: string;
	accountLogoUrl?: string;
	disabled?: boolean;
	attachmentsController: UseAIChatAttachmentsResult;
	onGetStarted: (submission: AgentBuilderStarterFormSubmission) => void;
}

export function AgentBuilderStarterForm({
	defaultWebsiteUrl,
	accountLogoUrl,
	disabled,
	attachmentsController,
	onGetStarted,
}: AgentBuilderStarterFormProps) {
	const id = React.useId();
	const [websiteUrl, setWebsiteUrl] = React.useState(defaultWebsiteUrl ?? '');
	const [useAccountLogo, setUseAccountLogo] = React.useState(true);
	const [instructions, setInstructions] = React.useState('');
	const [attachedFiles, setAttachedFiles] = React.useState<{ id: string; file: File }[]>([]);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const removedIdsRef = React.useRef<Set<string>>(new Set());

	const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = Array.from(e.target.files ?? []);
		e.target.value = '';
		if (selected.length === 0) {
			return;
		}
		const room = AI_CHAT_MAX_FILES - attachedFiles.length;
		if (selected.length > room) {
			toast.error(`You can attach up to ${AI_CHAT_MAX_FILES} files.`);
		}
		selected.slice(0, Math.max(room, 0)).forEach(file => {
			if (file.size > AI_CHAT_MAX_FILE_SIZE) {
				toast.error(`"${file.name}" is too large — the limit is ${Math.round(AI_CHAT_MAX_FILE_SIZE / 1_000_000)}MB.`);
				return;
			}
			const localId = crypto.randomUUID();
			setAttachedFiles(prev => [...prev, { id: localId, file }]);
			attachmentsController.upload(localId, file).then(uploaded => {
				if (uploaded === null && !removedIdsRef.current.has(localId)) {
					toast.error(`Failed to upload "${file.name}"`);
					setAttachedFiles(prev => prev.filter(f => f.id !== localId));
				}
			});
		});
	};

	const handleRemoveFile = (localId: string) => {
		removedIdsRef.current.add(localId);
		attachmentsController.forget(localId);
		setAttachedFiles(prev => prev.filter(f => f.id !== localId));
	};

	const handleSubmit = () => {
		onGetStarted({
			websiteUrl: websiteUrl.trim(),
			useAccountLogo: useAccountLogo && !!accountLogoUrl,
			instructions: instructions.trim(),
		});
	};

	return (
		<FieldGroup styles={[s.form]}>
			<Field>
				<FieldLabel htmlFor={`${id}-website`}>Client website</FieldLabel>
				<Input
					id={`${id}-website`}
					type='url'
					value={websiteUrl}
					onChange={e => setWebsiteUrl(e.target.value)}
					placeholder='https://www.example.com'
					disabled={disabled}
				/>
			</Field>
			{accountLogoUrl ? (
				<FieldLabel htmlFor={`${id}-logo`}>
					<Field orientation='horizontal'>
						<Avatar imgSrc={accountLogoUrl} />
						<FieldTitle>Use account logo as chatbot avatar</FieldTitle>
						<Checkbox
							id={`${id}-logo`}
							checked={useAccountLogo}
							onCheckedChange={checked => setUseAccountLogo(checked === true)}
							disabled={disabled}
						/>
					</Field>
				</FieldLabel>
			) : null}
			<Field>
				<FieldLabel htmlFor={`${id}-instructions`}>Instructions</FieldLabel>
				<TextArea
					id={`${id}-instructions`}
					value={instructions}
					onChange={e => setInstructions(e.target.value)}
					placeholder='e.g. "Focus on auto and home quotes" or "Visitors often ask about weekend hours"'
					maxLength={MAX_INSTRUCTIONS_LENGTH}
					rows={5}
					disabled={disabled}
					styles={[s.textArea]}
				/>
			</Field>
			<FieldContent>
				<input
					ref={fileInputRef}
					type='file'
					multiple
					accept={AI_CHAT_FILE_ACCEPT}
					onChange={handleFilesSelected}
					className={css(s.fileInput)}
					aria-hidden
					tabIndex={-1}
				/>
				<Button
					variant='secondary'
					styles={[s.attachButton]}
					onClick={() => fileInputRef.current?.click()}
					disabled={disabled || attachedFiles.length >= AI_CHAT_MAX_FILES}
				>
					<PaperClipIcon />
					Attach materials
				</Button>
				{attachedFiles.length > 0 ? (
					<Attachments variant='list' styles={[s.attachmentsList]}>
						{attachedFiles.map(({ id: fileId, file }) => (
							<Attachment
								key={fileId}
								data={{
									type: 'file',
									id: fileId,
									filename: file.name,
									mediaType: file.type,
									url: attachmentsController.uploadedById[fileId]?.url ?? '',
								}}
								onRemove={() => handleRemoveFile(fileId)}
							>
								<AttachmentPreview
									fallbackIcon={
										attachmentsController.pendingIds.has(fileId) ? <LoadingSpinner type='tiny' /> : undefined
									}
								/>
								<AttachmentInfo />
								<AttachmentRemove />
							</Attachment>
						))}
					</Attachments>
				) : null}
			</FieldContent>
			<Button styles={[s.submitButton]} onClick={handleSubmit} disabled={disabled}>
				Get Started
			</Button>
		</FieldGroup>
	);
}

const s = StyleSheet.create({
	form: {
		marginTop: THEME.spacing.md,
		maxWidth: 480,
	},
	fileInput: {
		display: 'none',
	},
	attachButton: {
		alignSelf: 'flex-start',
	},
	attachmentsList: {
		marginTop: THEME.spacing.sm,
	},
	submitButton: {
		alignSelf: 'flex-start',
	},
	textArea: {
		backgroundColor: white,
	},
});
